import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Loader2, Phone, GraduationCap, ShieldCheck, Sparkles } from 'lucide-react';
import { WHATSAPP_NUMBER, CONTACT_PHONE_DISPLAY, LOGO_URL } from '../mock';
import { trackEnrolmentConversion, trackPhoneCall, trackWhatsAppClick } from '../lib/tracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PROGRAMMES = ['Pre-Nursery', 'Nursery', 'Primary', 'Secondary', 'Foundation / A-Level'];

const wa = (t) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(t || 'Hello Whiteplains British Schools, I want to enrol.')}`;

const ApplyPage = () => {
  const [f, setF] = useState({ student_name: '', parent_name: '', email: '', phone: '', programme: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => { document.title = 'Enrol at Whiteplains British School | Admissions 2026/2027'; }, []);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!f.student_name || !f.parent_name || !f.email || !f.phone || !f.programme) {
      setErr('Please complete all required fields.'); return;
    }
    if (!/^\S+@\S+\.\S+$/.test(f.email)) { setErr('Please enter a valid email.'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/enrolments`, { ...f, source: 'google-ads' });
      setDone(res.data);
      trackEnrolmentConversion(res.data);
    } catch (er) {
      setErr(er?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const trust = [
    { icon: GraduationCap, t: 'Cambridge Pathway', s: 'Pre-Nursery to A-Level · 98% IGCSE pass rate' },
    { icon: ShieldCheck, t: 'Trusted since 2010', s: '15+ years of British-curriculum excellence in Abuja' },
    { icon: Sparkles, t: 'World-first innovations', s: 'VR gaming room · AI teacher · student microfinance bank' },
  ];

  return (
    <div className="min-h-screen bg-[#fbf7f2]">
      {/* Minimal top strip */}
      <div className="bg-[#0f0d0b] text-white py-3">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Whiteplains British School" className="h-9 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <div className="leading-tight">
              <div className="font-serif-display font-black text-[13px] tracking-[0.16em] uppercase">Whiteplains British School</div>
              <div className="font-cormorant italic text-[11px] text-[#f0c95a]">Bonitas et Disciplina</div>
            </div>
          </a>
          <a
            href={`tel:${CONTACT_PHONE_DISPLAY.replace(/\s/g, '')}`}
            onClick={() => trackPhoneCall(CONTACT_PHONE_DISPLAY)}
            className="hidden md:flex items-center gap-2 text-[12px] font-sans-pro uppercase tracking-[0.22em] text-[#f0c95a] hover:text-white"
          >
            <Phone size={13} /> {CONTACT_PHONE_DISPLAY}
          </a>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 md:py-16 grid md:grid-cols-12 gap-10">
        {/* Left — headline + trust */}
        <div className="md:col-span-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="font-sans-pro text-[11px] uppercase tracking-[0.4em] text-[#8f6417] font-semibold">Admissions · 2026 / 2027</div>
            <h1 className="mt-4 font-serif-display text-4xl md:text-6xl font-black leading-[1.05] text-[#1c1a17]">
              Enrol at <span className="italic text-[#b8331f]">Whiteplains British School</span>
            </h1>
            <p className="mt-5 text-[#4a4238] text-lg font-light leading-relaxed max-w-xl">
              A leading Cambridge Pathway international school in Abuja, Nigeria. Complete the form
              and our admissions team will contact you within 24 hours.
            </p>

            <div className="mt-8 space-y-4">
              {trust.map((it) => {
                const Icon = it.icon;
                return (
                  <div key={it.t} className="flex gap-4">
                    <div className="w-11 h-11 shrink-0 grid place-items-center border border-[#c8942a] text-[#8f6417]"><Icon size={20} /></div>
                    <div>
                      <div className="font-serif-display font-black text-[#1c1a17]">{it.t}</div>
                      <div className="text-sm text-[#4a4238]">{it.s}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 pt-6 border-t border-[#eadfc9] flex flex-wrap items-center gap-3">
              <a
                href={wa()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackWhatsAppClick}
                className="btn-primary !bg-[#25D366] !shadow-none inline-flex items-center gap-2"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.15-.174.199-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347z" /></svg>
                Chat on WhatsApp
              </a>
              <a
                href={`tel:${CONTACT_PHONE_DISPLAY.replace(/\s/g, '')}`}
                onClick={() => trackPhoneCall(CONTACT_PHONE_DISPLAY)}
                className="btn-outline inline-flex items-center gap-2"
              >
                <Phone size={14} /> {CONTACT_PHONE_DISPLAY}
              </a>
            </div>
          </motion.div>
        </div>

        {/* Right — form */}
        <div className="md:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="bg-white border border-[#c8942a]/40 shadow-2xl"
          >
            {done ? (
              <div className="p-8 text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-[#f3ead9] grid place-items-center text-[#8f6417]"><CheckCircle2 size={34} /></div>
                <h3 className="mt-5 font-serif-display text-2xl font-black">Application received</h3>
                <p className="mt-2 text-[#4a4238]">Thank you, {done.parent_name}. Our team will reach {done.student_name} within 24 hours.</p>
                <div className="mt-1 text-[11px] uppercase tracking-[0.3em] text-[#8f6417]">Ref · {done.id.slice(0, 8).toUpperCase()}</div>
                <a
                  href={wa(`Hello, I just applied for ${done.student_name} (${done.programme}). Ref ${done.id.slice(0,8).toUpperCase()}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={trackWhatsAppClick}
                  className="btn-primary !bg-[#25D366] !shadow-none mt-6 inline-flex"
                >Continue on WhatsApp <ArrowRight size={16} /></a>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 md:p-8 space-y-4">
                <h2 className="font-serif-display text-2xl font-black text-[#1c1a17]">Start your application</h2>
                <p className="text-[13px] text-[#8f6417] -mt-2">Takes 90 seconds. No commitment.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <F l="Student's full name *"><input value={f.student_name} onChange={set('student_name')} className="wbs-input" required /></F>
                  <F l="Programme *">
                    <select value={f.programme} onChange={set('programme')} className="wbs-input" required>
                      <option value="">Select…</option>
                      {PROGRAMMES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </F>
                  <F l="Parent / Guardian *"><input value={f.parent_name} onChange={set('parent_name')} className="wbs-input" required /></F>
                  <F l="Phone / WhatsApp *"><input value={f.phone} onChange={set('phone')} type="tel" className="wbs-input" required /></F>
                </div>
                <F l="Email *"><input value={f.email} onChange={set('email')} type="email" className="wbs-input" required /></F>
                <F l="Anything else? (optional)"><textarea value={f.message} onChange={set('message')} rows={2} className="wbs-input" /></F>
                {err && <div className="text-sm text-[#b8331f] bg-[#fdecea] border border-[#f3c1bb] px-4 py-2.5">{err}</div>}
                <button type="submit" disabled={busy} className="btn-primary w-full !justify-center disabled:opacity-70">
                  {busy ? (<><Loader2 size={16} className="animate-spin" /> Submitting</>) : (<>Submit Application <ArrowRight size={16} /></>)}
                </button>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#8f6417] text-center pt-1">
                  Your information stays private. No spam.
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const F = ({ l, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.28em] text-[#8f6417] font-sans-pro mb-1.5">{l}</div>
    {children}
  </label>
);

export default ApplyPage;
